import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import FSPopup from '../FSPopup';
import Field from '../Form/Field';
import Input from '../Form/Input';
import SelectProjects from '../Form/SelectProjects';
import Button from '../Button';
import ProjectsStore from '../../mobx/projects';
import MonitorsStore from '../../mobx/monitors';
import appStore from '../../mobx/app';
import { IMonitor } from '../../interfaces/monitor';
import ApiClient from '../../core/apiClient';
import AgentSetup from '../Monitor/AgentSetup';
import AgentSecretDialog from '../Monitor/AgentSecretDialog';

const AgentMonitor = observer(() => {
  const { t } = useTranslation();
  const history = useHistory();
  const [newProjectName, setNewProjectName] = useState('');
  const [selectProject, setSelectProject] = useState(ProjectsStore.projects.length > 0);
  const [form, setForm] = useState({
    url: '', name: '', project_id: -1, type: 'agent',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdMonitor, setCreatedMonitor] = useState<IMonitor>();
  const [secret, setSecret] = useState('');
  const pending = useRef(false);
  const active = useRef(true);
  useEffect(() => () => { active.current = false; }, []);

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };
  const selectChangeHandler = (value: string) => {
    setForm((current) => ({ ...current, project_id: ProjectsStore.projects.find((p) => p.name === value)?.id ?? -1 }));
  };
  const finish = (id: number) => {
    setSecret('');
    appStore.setPopupClosed();
    history.push(`/monitor/${id}`);
  };
  const onCreate = async () => {
    if (pending.current || createdMonitor) return;
    setError('');
    if (!form.name.trim() || !form.url.trim() || (selectProject ? form.project_id < 0 : !newProjectName.trim())) {
      setError(t('AGENT_REQUIRED'));
      return;
    }
    pending.current = true;
    setIsLoading(true);
    try {
      let projectId = form.project_id;
      if (!selectProject) {
        const projectRes = await ApiClient.createProject(newProjectName.trim());
        if (!active.current) return;
        if (!projectRes?.data?.project) { setError(t('AGENT_CREATE_ERROR')); return; }
        projectId = projectRes.data.project.id;
        ProjectsStore.projects.push(projectRes.data.project);
        setForm((current) => ({ ...current, project_id: projectId }));
        setSelectProject(true);
      }
      const address = form.url.trim();
      const res: any = await ApiClient.createMonitor({
        ...form,
        name: form.name.trim(),
        project_id: projectId,
        url: /^https?:\/\//i.test(address) ? address : `http://${address}`,
      });
      if (!active.current) return;
      if (!res?.data?.monitor) {
        const msg = res?.response?.data?.msg;
        setError(typeof msg === 'string' ? msg : (msg ? Object.values(msg).flat().join(' ') : t('AGENT_CREATE_ERROR')));
        return;
      }
      MonitorsStore.reachMonitors([res.data.monitor]);
      setCreatedMonitor(res.data.monitor);
      if (res.data.agent_secret) setSecret(res.data.agent_secret);
      else finish(res.data.monitor.id);
    } catch {
      if (active.current) setError(t('AGENT_CREATE_ERROR'));
    } finally {
      pending.current = false;
      if (active.current) setIsLoading(false);
    }
  };

  return (
    <FSPopup back={t('Back')}>
      <div className="agent-monitoring">
        <div className="agent-monitoring__title t-headline-2">{t('OpenSource Server Agent')}</div>
        <div className="agent-monitoring__desc t-input">{t('AGENT_UPGRADE')}</div>
        <form onSubmit={(event) => { event.preventDefault(); onCreate(); }}>
          <div className="agent-monitoring__block">
            <SelectProjects
              selectChangeHandler={selectChangeHandler}
              setNewProjectName={setNewProjectName}
              setSelectProjects={setSelectProject}
              value={form.project_id}
            />
            <Field label={t('Title')} required>
              <Input name="name" value={form.name} onChange={changeHandler} />
            </Field>
            <Field label={t('Enter server address')} required>
              <Input name="url" value={form.url} onChange={changeHandler} noHost placeholder="server.example.com" />
            </Field>
            <AgentSetup />
            {error && <p role="alert">{error}</p>}
            <Button text={t('Add monitoring')} bc="main18 last" size="big" onClick={onCreate} loading={isLoading} />
          </div>
        </form>
        {secret && createdMonitor && <AgentSecretDialog hostKey={createdMonitor.key} secret={secret} onClose={() => finish(createdMonitor.id)} />}
      </div>
    </FSPopup>
  );
});

export default AgentMonitor;
